import { Section } from '../../models';

/**
 * Which sections open which analysis tab.
 *
 * ONE home for every one of these lists, because they have two readers that
 * must agree:
 *
 *   - tabs-component.tsx renders the tab strip a practitioner sees in a
 *     section, and
 *   - section-notes-digest.tsx gathers those same tabs into the Notes tab, so
 *     their content can be pulled into the note.
 *
 * They were hand-kept copies. A section added to one and missed in the other
 * shows a tab on screen whose content Notes silently drops — which is exactly
 * what happened to Stress & Adrenal Balance's Insights tab between 2026-09-02
 * and 2026-09-14. Add a section HERE and both sides move together.
 *
 * Two things these lists deliberately do NOT decide:
 *
 *   - whether a tab has anything to say for a given test. Most call sites also
 *     check that the fired root-cause categories actually have rows for the
 *     tab (see *_TAB_REQUIRES_ROWS below); the digest is self-gating, since it
 *     only builds a card once it has found groups.
 *   - the left-hand Insights PANEL (the gauges: adrenal, thyroid, hydration,
 *     viscosity, nutrients). That is a computed component, not a tab of
 *     FurtherConsiderations rows, and it is gated per section in
 *     bloodwork-test-analysis-section.tsx and pdf-section.tsx.
 */

const has = (list: Section[], section: string): boolean =>
  list.some(s => s === section);

/* ── Insights ───────────────────────────────────────────────────────────────
 * The "Interpretation" rows.
 *
 * Blood Clotting & Flow is absent on purpose: its Insights tab is the computed
 * viscosity card, not Interpretation rows, so both readers special-case it.
 *
 * Stress & Adrenal Balance joined 2026-09-02 with 49 pattern rows (Logic +
 * Pattern Description) live in slava and reachable — possibleRootCauseCategoriesFor
 * has a STRESS_ADRENAL case and adrenalRootCauseById pushes the firing pattern
 * ids.
 */
export const SECTIONS_WITH_INSIGHTS_TAB: Section[] = [
  Section.RBC,
  Section.WBC,
  Section.GLUCOSE,
  Section.STRESS_ADRENAL,
];

/**
 * Sections whose Insights tab needs rows on a fired category before it opens,
 * so a test with no findings never shows an empty pane.
 *
 * The original trio is exempt rather than un-checked: their Interpretation rows
 * have shipped for as long as the tab has, and making them prove it could close
 * a tab that opens today. A section joining the list above should start here.
 */
export const INSIGHTS_TAB_REQUIRES_ROWS: Section[] = [Section.STRESS_ADRENAL];

export const sectionHasInsightsTab = (section: string): boolean =>
  has(SECTIONS_WITH_INSIGHTS_TAB, section);

export const insightsTabRequiresRows = (section: string): boolean =>
  has(INSIGHTS_TAB_REQUIRES_ROWS, section);

/* ── Possible Root Causes ───────────────────────────────────────────────────
 * Everywhere but RBC, which splits the same ground across its own Additional
 * Testing / Signs and Symptoms / Diet / Lifestyle tabs, and the practitioner's
 * own markers, which have no root-cause categories behind them at all.
 */
export const sectionHasPossibleRootCausesTab = (section: string): boolean =>
  section !== Section.RBC && section !== Section.CUSTOM_MARKERS;

/* ── Additional Testing / Signs and Symptoms / Diet / Lifestyle ─────────────
 * RBC's four extra guidance tabs. They stand or fall together.
 */
export const sectionHasGuidanceTabs = (section: string): boolean =>
  section === Section.RBC;

/* ── Further Considerations ────────────────────────────────────────────────*/
export const SECTIONS_WITH_FURTHER_CONSIDERATIONS_TAB: Section[] = [
  Section.RBC,
  Section.WBC,
  Section.GLUCOSE,
];

export const sectionHasFurtherConsiderationsTab = (section: string): boolean =>
  has(SECTIONS_WITH_FURTHER_CONSIDERATIONS_TAB, section);

/* ── About ─────────────────────────────────────────────────────────────────
 * Per-marker plain-language descriptions. Everywhere except the practitioner's
 * own markers, which have none.
 */
export const sectionHasAboutTab = (section: string): boolean =>
  section !== Section.CUSTOM_MARKERS;

/* ── Supplements ───────────────────────────────────────────────────────────
 * An allowlist rather than "any section with Supplements rows": a section
 * belongs here once its rows are authored and reviewed, and an accidental row
 * elsewhere should not open a tab on its own.
 *
 * This matters more than the others. The Holobiome by-system cards walk every
 * section in SECTION_HEADINGS, and everything they list starts TICKED on the
 * client's Recommended Supplements page — so a section's rows can reach a
 * client document before this tab ever shows them to the practitioner. That is
 * why NUTRIENTS (2026-08), INFLAMMATION_OXIDATIVE_STRESS (2026-08, one marker:
 * 13040 ▲ hs-CRP at subCategoryId 3) and STRESS_ADRENAL (2026-08, 19 pattern
 * rows at cat 90xxx) had to ship their listing and their rows together.
 *
 * Digestion has 15 authored rows (cat 85000+) and is still absent: nothing
 * reaches them, because possibleRootCauseCategoriesFor has no DIGESTION case
 * and its default branch hands back the global root-cause list, whose ids never
 * match an 85000+ row. It joins when that detection lands.
 *
 * Note the Supplements tab is ALSO behind supplementsVisible() — the
 * fail-closed env check shared by all supplement features — which this list
 * does not speak to.
 */
export const SECTIONS_WITH_SUPPLEMENTS_TAB: Section[] = [
  Section.RBC,
  Section.WBC,
  Section.GLUCOSE,
  Section.NUTRIENTS,
  Section.INFLAMMATION_OXIDATIVE_STRESS,
  Section.STRESS_ADRENAL,
];

export const sectionHasSupplementsTab = (section: string): boolean =>
  has(SECTIONS_WITH_SUPPLEMENTS_TAB, section);

/* ── References ────────────────────────────────────────────────────────────
 * A section belongs here once its References rows are authored and its markers'
 * Causes text has somewhere to point.
 *
 * Liver / Biliary, Nutrients, Electrolytes and Stress & Adrenal Balance joined
 * through 2026-08 as their research passes landed. Electrolytes (sodium
 * 9000/9010, potassium 9020/9030) and Stress & Adrenal Balance (Na/K ratio
 * 90000/90010) had to ship their rows and their listing together: their Causes
 * text carries inline "(n)" markers, which render as plain unlinked numbers
 * until the References rows exist to resolve them. Electrolytes has since left
 * again, below.
 *
 * Thyroid and Digestion are deliberately absent. Both have reference rows
 * authored, but Digestion has no possibleRootCauseCategoriesFor case, so
 * nothing reaches its 85000+ rows anyway; they join when those sections do.
 *
 * Immune Activity (WBC), Electrolytes and Liver / Biliary LEFT on 2026-10-10. Its ten PRC References rows
 * (11230-11380) were the last thing holding the tab open there, and they are
 * gone: those markers' Causes bullets now carry their PMIDs inline, which is
 * the convention settled with HbA1c and homocysteine. A PRC marker row cites
 * its own sources and contributes nothing to this tab. Nutrients is in the
 * same state (homocysteine, 2026-10-08) and stays listed only because nobody
 * has swept it. WBC rejoins the day immune PATTERN references are authored.
 * Electrolytes went the same way the same day: all six PRC References rows
 * (9000-9050, sodium, potassium and chloride) are inline PMIDs now, and the
 * section has no pattern references of its own, so nothing is left to show.
 * Its "(n)" markers are gone too, so the hazard the paragraph above describes
 * no longer applies to it. Liver / Biliary followed the same day (4132-4190:
 * AST/ALT, LDH, HSI, FIB-4), and its pattern set is still unbuilt - there are
 * no 95xxx pattern or 96xxx reference rows in the table at all - so it rejoins
 * when that research lands, not before.
 *
 * Stress & Adrenal Balance is the counter-example and STAYS: its 90xxx ids
 * carry a Pattern References row alongside the PRC one, and only the PRC row
 * was removed.
 */
export const SECTIONS_WITH_REFERENCES_TAB: Section[] = [
  Section.GLUCOSE,
  Section.PLATELETS,
  Section.NUTRIENTS,
  Section.STRESS_ADRENAL,
];

export const sectionHasReferencesTab = (section: string): boolean =>
  has(SECTIONS_WITH_REFERENCES_TAB, section);
